import { useEffect, useState } from "react";
import { Row, Col, Card, Button, Modal, Form, Table } from "react-bootstrap";
import Breadcrumb from "app/components/Breadcrumb";
import api from "app/services/api";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default function ProductCategory() {
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);

  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const [category_name, setCategoryName] = useState("");
  const [status, setStatus] = useState("active");

  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [showAll, setShowAll] = useState(true);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = () => {
    api
      .get("/api/product-categories", {
        withCredentials: true,
      })
      .then((res) => {
        setCategories(res.data.productCategories || []);
      })
      .catch(() => {
        setCategories([]);
      });
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    const tableColumn = ["No", "Category Name", "Status"];
    const tableRows = categories.map((item, index) => [
      index + 1,
      item.category_name,
      item.status,
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
    });
    doc.save("product-categories.pdf");
  };

  const resetForm = () => {
    setCategoryName("");
    setStatus("active");
  };

  const handleAddCategory = () => {
    if (!category_name.trim()) {
      setErrorMsg("Please enter category name");
      setTimeout(() => setErrorMsg(""), 3000);
      return;
    }

    api
      .post(
        "/api/product-categories",
        { category_name, status },
        { withCredentials: true }
      )
      .then((res) => {
        setSuccessMsg("Product category added successfully");
        fetchCategories();
        resetForm();
        setShowModal(false);
        setTimeout(() => setSuccessMsg(""), 3000);
      })
      .catch(() => {
        setErrorMsg("Failed to add product category");
        setTimeout(() => setErrorMsg(""), 3000);
      });
  };

  const handleUpdateCategory = () => {
    if (!category_name.trim()) {
      setErrorMsg("Please enter category name");
      setTimeout(() => setErrorMsg(""), 3000);
      return;
    }

    api
      .put(
        "/api/product-categories/" + editData.id,
        { category_name, status },
        { withCredentials: true }
      )
      .then(() => {
        setSuccessMsg("Product category updated successfully");
        fetchCategories();
        resetForm();
        setEditData(null);
        setShowModal(false);
        setTimeout(() => setSuccessMsg(""), 3000);
      })
      .catch(() => {
        setErrorMsg("Failed to update product category");
        setTimeout(() => setErrorMsg(""), 3000);
      });
  };

  const handleStatusToggle = (item) => {
    const newStatus = item.status === "active" ? "inactive" : "active";

    api
      .put(
        "/api/product-categories/" + item.id,
        { ...item, status: newStatus },
        { withCredentials: true }
      )
      .then(() => {
        setCategories(categories.map((cat) => {
          if (cat.id === item.id) {
            return { ...cat, status: newStatus };
          }
          return cat;
        }));
      });
  };

  const filteredCategories = categories.filter((item) =>
    item.category_name?.toLowerCase().includes(search.toLowerCase())
  );

  const totalRows = filteredCategories.length;
  const totalPages = rowsPerPage >= totalRows ? 1 : Math.ceil(totalRows / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedCategories = showAll
    ? filteredCategories
    : filteredCategories.slice(startIndex, startIndex + rowsPerPage);

  return (
    <section>
      <Breadcrumb
        routeSegments={[
          { name: "Dashboard", path: "/" },
          { name: "Product", path: "/product/products" },
          { name: "Product Categories" },
        ]}
      />

      {successMsg && <div className="alert alert-success">{successMsg}</div>}
      {errorMsg && <div className="alert alert-danger">{errorMsg}</div>}

      <Card body className="mb-3">
        <div className="d-flex justify-content-between align-items-center">
          <Button
            style={{ backgroundColor: "#663399", border: "none" }}
            onClick={() => {
              setEditData(null);
              resetForm();
              setShowModal(true);
            }}
          >
            Add Category
          </Button>

          <div className="d-flex align-items-center">
            <input
              type="text"
              placeholder="Search Category"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: "250px",
                height: "38px",
                paddingLeft: "10px",
                border: "1px solid #d1d1d1",
              }}
            />
            <Button
              onClick={handleDownloadPDF}
              style={{ backgroundColor: "#663399", border: "none", marginLeft: "10px" }}
            >
              Download PDF
            </Button>
          </div>
        </div>
      </Card>

      <Card body>
        <Table bordered hover>
          <thead style={{ backgroundColor: "#cfc7d9" }}>
            <tr>
              <th>No</th>
              <th>Category Name</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {paginatedCategories.map((item, index) => (
              <tr key={item.id}>
                <td>{startIndex + index + 1}</td>
                <td>{item.category_name}</td>
                <td>
                  <span
                    className={"badge " + (item.status === "active" ? "bg-success" : "bg-secondary")}
                    style={{ cursor: "pointer" }}
                    onClick={() => handleStatusToggle(item)}
                  >
                    {item.status}
                  </span>
                </td>
                <td>
                  <div className="d-flex gap-2">
                    <Button
                      style={{ backgroundColor: "#663399", border: "none" }}
                      onClick={() => {
                        setEditData(item);
                        setCategoryName(item.category_name);
                        setStatus(item.status);
                        setShowModal(true);
                      }}
                    >
                      Edit
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {paginatedCategories.length === 0 && (
              <tr>
                <td colSpan="4" className="text-center">No categories found.</td>
              </tr>
            )}
          </tbody>
        </Table>

        <div className="d-flex justify-content-end align-items-center mt-3">
          <span className="me-2">Rows per page</span>
          <Form.Select
            value={showAll ? "all" : rowsPerPage}
            onChange={(e) => {
              if (e.target.value === "all") {
                setShowAll(true);
                setRowsPerPage(filteredCategories.length || 5);
              } else {
                setShowAll(false);
                setRowsPerPage(Number(e.target.value));
              }
              setCurrentPage(1);
            }}
            style={{ width: "90px", marginRight: "15px" }}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value="all">All</option>
          </Form.Select>

          <Button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            Prev
          </Button>
          <span className="mx-3">
            {currentPage} / {totalPages || 1}
          </span>
          <Button
            disabled={currentPage >= totalPages}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            Next
          </Button>
        </div>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editData ? "Edit Category" : "Add Category"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={12} className="mb-3">
                <Form.Label>
                  Category Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter category name"
                  value={category_name}
                  onChange={(e) => setCategoryName(e.target.value)}
                />
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
          <Button
            style={{ backgroundColor: "#663399", border: "none" }}
            onClick={editData ? handleUpdateCategory : handleAddCategory}
          >
            {editData ? "Update" : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
    </section>
  );
}
